import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ExternalLink } from 'lucide-react';
import { handleCompanyNameClick } from '../utils/hubspot';

interface ClickableMarkdownProps {
  content: string;
  className?: string;
}

const ClickableMarkdown: React.FC<ClickableMarkdownProps> = ({ content, className }) => {
  // Enhanced text processing using a simple regex replacement approach
  const processClickableIds = (text: string): string => {
    // IMPORTANT: Process more specific patterns first, then general patterns

    // Handle format: "**Meeting**: Gong Meeting 12345678901234567890" (most specific)
    // Allow flexible whitespace between : and Gong, and between Gong Meeting and ID
    text = text.replace(/\*\*Meeting\*\*:\s+Gong\s+Meeting\s+(\d{10,20})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched **Meeting**: Gong Meeting pattern, ID:', id);
      return `**Meeting**: [Gong Meeting ${id}](https://smartcat-gong-meeting/${id})`;
    });

    // Handle bold Gong Meeting references with colons (format: "**Gong Meeting:** 12345678901234567890")
    text = text.replace(/\*\*Gong Meeting\*\*:\s*(\d{10,20})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched **Gong Meeting**: pattern, ID:', id);
      return `[Gong Meeting ${id}](https://smartcat-gong-meeting/${id})`;
    });

    // Handle Gong Meeting with timestamp (format: "Gong Meeting 12345678901234567890@120" where 120 is seconds)
    // This creates timestamp-highlighted links that jump to specific moments in the call
    // Support Gong IDs from 10-20 digits to handle various formats
    // Use shorter link text to prevent markdown wrapping issues
    text = text.replace(/Gong Meeting #?(\d{10,20})@(\d+)\b/gi, (match, id, timestamp) => {
      console.log('[ClickableMarkdown] Matched Gong Meeting with timestamp, ID:', id, 'timestamp:', timestamp);
      // Shorten the ID display to last 8 digits to prevent wrapping, but keep full ID in URL
      const shortId = id.length > 8 ? '...' + id.slice(-8) : id;
      return `[Gong Meeting ${shortId}@${timestamp}s](https://smartcat-gong-meeting/${id}?ts=${timestamp})`;
    });

    // Handle explicit Gong Meeting references (format: "Gong Meeting #12345678901234567890" or "Gong Meeting 12345678901234567890")
    // Support Gong IDs from 10-20 digits to handle various formats (least specific, runs last)
    // Use shorter link text to prevent markdown wrapping issues
    text = text.replace(/Gong Meeting #?(\d{10,20})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched basic Gong Meeting pattern, ID:', id);
      // Shorten the ID display to last 8 digits to prevent wrapping, but keep full ID in URL
      const shortId = id.length > 8 ? '...' + id.slice(-8) : id;
      return `[Gong Meeting ${shortId}](https://smartcat-gong-meeting/${id})`;
    });

    // Handle explicit Sales Cycle references (format: "Sales Cycle #12345678901" or "Sales Cycle 12345678901")
    text = text.replace(/Sales Cycle #?(\d{10,12})\b/gi, (match, id) => {
      return `[Sales Cycle ${id}](https://smartcat-sales-cycle/${id})`;
    });

    // Handle bold Sales Cycle references with colons (format: "**Sales Cycle:** 12345678901")
    text = text.replace(/\*\*Sales Cycle\*\*:\s*(\d{10,12})\b/gi, (match, id) => {
      return `[Sales Cycle ${id}](https://smartcat-sales-cycle/${id})`;
    });

    // Handle explicit Deal references (format: "Deal #12345678901" or "Deal 12345678901")
    text = text.replace(/Deal #?(\d{10,12})\b/gi, (match, id) => {
      return `[Deal ${id}](https://smartcat-deal/${id})`;
    });

    // Handle bold Deal references with colons (format: "**Deal:** 12345678901")
    text = text.replace(/\*\*Deal\*\*:\s*(\d{10,12})\b/gi, (match, id) => {
      return `[Deal ${id}](https://smartcat-deal/${id})`;
    });

    // Handle deal array format from logs (format: "Deal #['12345678901']")
    text = text.replace(/Deal\s*#\[\'(\d{10,12})\'\]/gi, (_, id) => {
      return `[Deal ${id}](https://smartcat-deal/${id})`;
    });

    // Handle malformed references like "Deal Sales Cycle 12345678901"
    text = text.replace(/Deal\s+Sales\s+Cycle\s+(\d{10,12})\b/gi, (_, id) => {
      return `[Sales Cycle ${id}](https://smartcat-sales-cycle/${id})`;
    });

    // Handle standalone hash references (format: #12345678901) - assume sales cycle
    text = text.replace(/#(\d{10,12})\b/g, (_, id) => {
      return `[Sales Cycle ${id}](https://smartcat-sales-cycle/${id})`;
    });

    // Handle Zendesk ticket references (format: "Zendesk Ticket #123456" or "Zendesk Ticket 123456")
    text = text.replace(/Zendesk Ticket #?(\d{4,10})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched Zendesk Ticket pattern, ID:', id);
      return `[Zendesk Ticket ${id}](https://smartcat-zendesk-ticket/${id})`;
    });

    // Handle bold Zendesk Ticket references (format: "**Zendesk Ticket:** 123456")
    text = text.replace(/\*\*Zendesk Ticket\*\*:\s*#?(\d{4,10})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched **Zendesk Ticket**: pattern, ID:', id);
      return `[Zendesk Ticket ${id}](https://smartcat-zendesk-ticket/${id})`;
    });

    // Handle generic Ticket references with Zendesk context (format: "Ticket #123456" or "Ticket 123456")
    // Only match shorter IDs (4-10 digits) to distinguish from Sales Cycle/Deal IDs (10-12 digits)
    text = text.replace(/\bTicket #?(\d{4,10})\b/gi, (match, id) => {
      console.log('[ClickableMarkdown] Matched Ticket pattern, ID:', id);
      return `[Ticket ${id}](https://smartcat-zendesk-ticket/${id})`;
    });

    // Handle deal names that are already formatted as clickable references from the backend
    // These come from primary_clickable_reference field and are already formatted as deal names
    // Pattern: Look for text that looks like deal names (contains common deal name patterns)
    // This is a fallback for when the AI sends deal names directly without the "Deal #" prefix
    
    return text;
  };

  const processedContent = processClickableIds(content);

  return (
    <div className={className}>
      <ReactMarkdown 
        components={{
          h1: ({children}: any) => <h1 className="text-lg font-bold mb-2 text-brand-900">{children}</h1>,
          h2: ({children}: any) => <h2 className="text-base font-bold mb-2 text-brand-900">{children}</h2>,
          h3: ({children}: any) => <h3 className="text-sm font-bold mb-1 text-brand-800">{children}</h3>,
          p: ({children}: any) => <p className="text-sm mb-2 text-brand-800 leading-relaxed">{children}</p>,
          ul: ({children}: any) => <ul className="text-sm mb-2 ml-4 list-disc text-brand-800">{children}</ul>,
          ol: ({children}: any) => <ol className="text-sm mb-2 ml-4 list-decimal text-brand-800">{children}</ol>,
          li: ({children}: any) => <li className="mb-1 text-brand-800">{children}</li>,
          strong: ({children}: any) => <strong className="font-semibold text-brand-900">{children}</strong>,
          em: ({children}: any) => <em className="italic text-brand-700">{children}</em>,
          code: ({children}: any) => <code className="bg-brand-100 px-1 py-0.5 rounded text-xs font-mono text-brand-800">{children}</code>,
          blockquote: ({children}: any) => <blockquote className="border-l-4 border-accent-blue/30 pl-3 py-1 bg-accent-blue/10 text-brand-700 text-sm italic">{children}</blockquote>,
          a: ({href, children}: any) => {
            console.log('Link component called with href:', href, 'children:', children);

            // Handle Gong Meeting links (with optional timestamp highlighting)
            if (href?.startsWith('https://smartcat-gong-meeting/')) {
              const urlParts = href.replace('https://smartcat-gong-meeting/', '').split('?ts=');
              const gongId = urlParts[0];
              const timestamp = urlParts[1];

              console.log('Processing Gong meeting link with ID:', gongId, 'timestamp:', timestamp);

              let gongUrl = `https://us-72592.app.gong.io/call?id=${gongId}`;

              // Add timestamp highlighting if present
              if (timestamp) {
                const highlightsJson = `[{"type":"SHARE","from":"${timestamp}","to":"${timestamp}"}]`;
                const encodedHighlights = encodeURIComponent(highlightsJson);
                gongUrl += `&highlights=${encodedHighlights}`;
              }

              return (
                <a
                  href={gongUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 px-1 py-0.5 rounded transition-colors font-medium inline-block max-w-full truncate align-middle"
                  title={timestamp ? `${children} - Open Gong recording at ${timestamp}s in new tab` : `${children} - Open Gong recording in new tab`}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
                </a>
              );
            }

            // Handle custom smartcat URLs (legacy format)
            if (href?.startsWith('https://smartcat-sales-cycle/')) {
              const salesCycleId = href.replace('https://smartcat-sales-cycle/', '');
              console.log('Processing sales cycle link with ID:', salesCycleId);
              return (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCompanyNameClick(undefined, salesCycleId, `Sales Cycle ${salesCycleId}`);
                  }}
                  className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 px-1 py-0.5 rounded transition-colors font-medium inline-block max-w-full truncate align-middle border-none bg-transparent cursor-pointer"
                  title={`${children} - Open Sales Cycle ${salesCycleId} in HubSpot (opens in new tab)`}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
                </button>
              );
            } else if (href?.startsWith('https://smartcat-deal/')) {
              const dealId = href.replace('https://smartcat-deal/', '');
              console.log('Processing deal link with ID:', dealId);
              return (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCompanyNameClick(dealId, undefined, `Deal ${dealId}`);
                  }}
                  className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 px-1 py-0.5 rounded transition-colors font-medium inline-block max-w-full truncate align-middle border-none bg-transparent cursor-pointer"
                  title={`${children} - Open Deal ${dealId} in HubSpot (opens in new tab)`}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
                </button>
              );
            } else if (href?.startsWith('https://smartcat-zendesk-ticket/')) {
              const ticketId = href.replace('https://smartcat-zendesk-ticket/', '');
              console.log('Processing Zendesk ticket link with ID:', ticketId);
              const zendeskUrl = `https://helpsc.zendesk.com/agent/tickets/${ticketId}`;
              return (
                <a
                  href={zendeskUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 px-1 py-0.5 rounded transition-colors font-medium inline-block max-w-full truncate align-middle"
                  title={`${children} - Open Zendesk ticket in new tab`}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
                </a>
              );
            }

            // Handle direct HubSpot URLs (current format from AI responses)
            if (href?.includes('app.hubspot.com/contacts/4950983/record/')) {
              console.log('Processing HubSpot link:', href);
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 px-1 py-0.5 rounded transition-colors font-medium inline-block max-w-full truncate align-middle"
                  title={`${children} - Opens in new tab`}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
                </a>
              );
            }
            
            // Regular links - open in new tab with proper indicators
            return (
              <a 
                href={href} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-blue hover:text-accent-blue/80 transition-colors inline-block max-w-full truncate align-middle"
                title={`${children} - Opens in new tab`}
              >
                {children}
                <ExternalLink className="h-3 w-3 ml-1 inline-block align-middle" />
              </a>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default ClickableMarkdown;